import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import RespondentDetailSheet from '../dialogs/RespondentDetailSheet';
import { INVITATIONS, RESPONDENT_DRILL_DOWN, statusVariant, prettyDate } from '../../fixtures';
import { Eye, ShieldCheck, AlertTriangle, Flag } from 'lucide-react';

const ConsultantTab = ({ campaign }) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const completed = INVITATIONS.filter((i) => i.status === 'completed');

  return (
    <div className='space-y-6'>
      <Alert>
        <ShieldCheck size={14} strokeWidth={1.5} />
        <AlertTitle>Consultant audit access</AlertTitle>
        <AlertDescription>
          You can view raw responses, calculated per-component values, and the emission factors applied. Flag or exclude
          anomalous responses before final calculation. This tab is only visible to CYF consultants and whitelisted
          admins.
        </AlertDescription>
      </Alert>

      <div className='grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Submitted responses</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              Click a row to inspect raw answers, per-component kWh, and the factor year applied.
            </p>
          </CardHeader>
          <Table wrapperClassName='rounded-none border-0 shadow-none'>
            <TableHeader>
              <TableRow>
                <TableHead>Respondent</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead className='text-right'>WFH tCO2e</TableHead>
                <TableHead className='text-right'>Commute tCO2e</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {completed.map((inv) => {
                const isFlagged = !!inv.flagged;
                return (
                  <TableRow
                    key={inv.id}
                    className='cursor-pointer'
                    onClick={() => setSheetOpen(true)}>
                    <TableCell>
                      <div className='font-medium'>{inv.name}</div>
                      <div className='text-xs text-muted-foreground'>{inv.email}</div>
                    </TableCell>
                    <TableCell className='text-sm'>{inv.location}</TableCell>
                    <TableCell className='text-xs text-muted-foreground'>{prettyDate(inv.completedAt)}</TableCell>
                    <TableCell>
                      {isFlagged ? (
                        <Badge variant='warning' size='sm'>
                          <AlertTriangle size={10} strokeWidth={1.5} />
                          {inv.flagReason}
                        </Badge>
                      ) : (
                        <Badge variant='success' size='sm'>
                          OK
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className='text-right tabular-nums text-sm'>0.25</TableCell>
                    <TableCell className='text-right tabular-nums text-sm'>0.13</TableCell>
                    <TableCell className='text-right'>
                      <div className='flex items-center justify-end gap-1'>
                        <Button variant='ghost' size='icon' title='Inspect'>
                          <Eye size={14} strokeWidth={1.5} />
                        </Button>
                        <Button variant='ghost' size='icon' title='Flag for review'>
                          <Flag size={14} strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <div className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle className='text-sm'>Engine &amp; factors</CardTitle>
            </CardHeader>
            <CardContent className='space-y-2 text-xs'>
              <div className='flex items-center justify-between'>
                <span className='text-muted-foreground'>Engine version</span>
                <span className='font-mono'>v1.0.3</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-muted-foreground'>DEFRA factor year</span>
                <span className='font-mono'>2024</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-muted-foreground'>Home Working entity</span>
                <span className='font-mono'>550085</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-muted-foreground'>Commuting entity</span>
                <span className='font-mono'>234152</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-muted-foreground'>Last recalculated</span>
                <span>{prettyDate(campaign.closedAt || new Date().toISOString())}</span>
              </div>
              <Button variant='outline' size='sm' className='w-full mt-3'>
                Trigger recalculation
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className='text-sm'>Methodology flags</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3 text-xs'>
              <div className='flex gap-2'>
                <AlertTriangle size={12} strokeWidth={1.5} className='text-warning flex-shrink-0 mt-0.5' />
                <div>
                  <div className='font-semibold'>Don't Know fuel → 0 kWh</div>
                  <div className='text-muted-foreground'>8 respondents. OQ-1 pending methodology sign-off.</div>
                </div>
              </div>
              <div className='flex gap-2'>
                <AlertTriangle size={12} strokeWidth={1.5} className='text-warning flex-shrink-0 mt-0.5' />
                <div>
                  <div className='font-semibold'>Long commute &gt;200km</div>
                  <div className='text-muted-foreground'>1 respondent. Manual review recommended.</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <RespondentDetailSheet open={sheetOpen} onOpenChange={setSheetOpen} respondent={RESPONDENT_DRILL_DOWN} />
    </div>
  );
};

export default ConsultantTab;
